import { Link } from 'react-router-dom'
import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Paper,
  useMediaQuery,
  useTheme
} from '@mui/material'
import { Home, Payment, Description } from '@mui/icons-material'
import { useLocation } from 'react-router-dom'
import { IconMeterReading2, IconPayment2 } from '../assets/Icons'

interface NavItem {
  to: string
  icon: JSX.Element
  text: string
  shortText?: string
}

const navItems: NavItem[] = [
  { to: '/', icon: <Home />, text: 'Übersicht', shortText: 'Übersicht' },
  {
    to: '/vertraege',
    icon: <Description />,
    text: 'Meine Verträge',
    shortText: 'Verträge'
  },
  {
    to: '/zaehlerstaende',
    icon: <IconMeterReading2 />,
    text: 'Zählerstände',
    shortText: 'Zähler'
  },
  {
    to: '/rechnungen',
    icon: <Payment />,
    text: 'Zahlung & Rechnungsadresse',
    shortText: 'Zahlung'
  },
  {
    to: '/abschlaege',
    icon: <IconPayment2 />,
    text: 'Abschläge',
    shortText: 'Abschläge'
  }
]

interface SidebarNavProps {
  handleLogout: () => void
}

const SidebarNav: React.FC<SidebarNavProps> = ({ handleLogout }) => {
  const location = useLocation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isCompact = useMediaQuery(theme.breakpoints.down('lg'))

  return (
    <Paper 
      variant="outlined"
      sx={{
        mb: { xs: 2, md: 0 }
      }}
    >
      <List 
        disablePadding
        sx={{
          display: { xs: 'flex', md: 'block' },
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          justifyContent: { xs: 'center', sm: 'flex-start' },
          overflowX: { xs: 'auto', md: 'visible' }
        }}
      >
        {navItems.map((item: NavItem) => (
          <Link key={item.to} to={item.to} style={{ textDecoration: 'none' }}>
            <ListItemButton 
              selected={location.pathname === item.to}
              sx={{
                minWidth: { xs: 'auto', md: '100%' },
                px: { xs: 1.5, sm: 2, md: 2 },
                py: { xs: 1, md: 1.5 },
                flexDirection: { xs: 'column', sm: 'row', md: 'row' },
                alignItems: 'center'
              }}
            >
              <ListItemIcon 
                sx={{ 
                  minWidth: { xs: 'auto', md: 56 },
                  mr: { xs: 0, sm: 1, md: 2 },
                  mb: { xs: 0.5, sm: 0 }
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={isMobile ? item.shortText : item.text} 
                sx={{ 
                  color: 'text.primary',
                  '& .MuiTypography-root': {
                    fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                    textAlign: { xs: 'center', sm: 'left' }
                  }
                }} 
              />
            </ListItemButton>
          </Link>
        ))}
        <Divider sx={{ display: { xs: 'none', md: 'block' } }} />
        <Link to="/helpandcontact" style={{ textDecoration: 'none' }}>
          <ListItemButton
            sx={{
              px: { xs: 1.5, sm: 2, md: 2 },
              py: { xs: 1, md: 1.5 }
            }}
          >
            <ListItemText
              primary={isMobile ? 'Hilfe' : 'Hilfe & Kontakt'}
              sx={{ 
                color: 'text.primary',
                '& .MuiTypography-root': {
                  fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' }
                }
              }}
            />
          </ListItemButton>
        </Link>

        <ListItemButton
          onClick={handleLogout}
          sx={{
            display: { xs: 'none', md: 'flex' },
            px: { xs: 1.5, sm: 2, md: 2 },
            py: { xs: 1, md: 1.5 }
          }}
        >
          <ListItemText
            primary="Logout"
            sx={{ color: 'text.primary' }}
          />
        </ListItemButton>
      </List>
    </Paper>
  )
}

export default SidebarNav
